import { FilterInfo } from "@/store/deduction/deductionFilter";
import { ApprovalFilterInfo } from "@/store/approvals";
import { PromotionsFilterInfo } from "@/store/promotion";
import { AccountingRemittanceFilterInfo } from "@/store/accounting";
import { DisputeTrackingFilterInfo } from "@/store/disputeTracking";
import { SelectOption } from "@ir/ui";
import { FilterDateRange } from "./interfaces/IDeductionFilter";

export type FilterSetInfo = FilterInfo | ApprovalFilterInfo | PromotionsFilterInfo | AccountingRemittanceFilterInfo | DisputeTrackingFilterInfo;

export function isPromotionsFilterInfo(info: FilterSetInfo): info is PromotionsFilterInfo {
  return "promoType" in info;
}

export function isDisputeTrackingFilterInfo(info: FilterSetInfo): info is DisputeTrackingFilterInfo {
  return "disputeTrackingStatus" in info;
}

export function isApprovalFilterInfo(info: FilterSetInfo): info is ApprovalFilterInfo {
  return "glCode" in info;
}

export function isAccountingRemittanceFilterInfo(info: FilterSetInfo): info is AccountingRemittanceFilterInfo {
  return "purchaser" in info && !("glCode" in info) && !("promoType" in info) && !("priority" in info) && !("dc" in info) && !("disputeTrackingStatus" in info);
}

export function getEffectiveCheckDateRange(
  filterInfo: FilterSetInfo,
  isFilterApplied: boolean,
  datesMatchDefault: boolean,
  defaultCheckDateRangePreset: FilterDateRange | null
): FilterDateRange | null {
  if (!isFilterApplied && datesMatchDefault && "checkDateRange" in filterInfo) {
    return defaultCheckDateRangePreset;
  }

  if ("checkDateRange" in filterInfo) {
    return filterInfo.checkDateRange as FilterDateRange | null;
  }

  return null;
}

function clearCheckDateFilter(info: FilterSetInfo): FilterSetInfo | null {
  if (!("checkFromDate" in info)) {
    return null;
  }

  return { ...info, checkFromDate: null, checkToDate: null, checkDateRange: null };
}

function clearInvoiceDateFilter(info: FilterSetInfo): FilterSetInfo | null {
  if (!("invoiceFromDate" in info)) {
    return null;
  }

  return { ...info, invoiceFromDate: null, invoiceToDate: null, invoiceDateRange: null };
}

function clearStartDateFilter(info: FilterSetInfo): FilterSetInfo | null {
  if (!isPromotionsFilterInfo(info)) {
    return null;
  }

  return { ...info, startFromDate: null, startToDate: null, startDateRange: null };
}

function clearSearchFilter(info: FilterSetInfo): FilterSetInfo | null {
  if (!("search" in info)) {
    return null;
  }

  return { ...info, search: null };
}

function clearArrayFilterKey(filterInfo: FilterSetInfo, key: string): FilterSetInfo {
  const newFilterInfo = { ...filterInfo } as FilterSetInfo;

  for (const filterKey of Object.keys(filterInfo) as (keyof typeof filterInfo)[]) {
    if (key.includes(filterKey) || filterKey === key) {
      const value = Array.isArray(filterInfo[filterKey]) ? ([] as SelectOption[]) : null;
      (newFilterInfo as unknown as Record<string, unknown>)[filterKey] = value;
    }
  }

  return newFilterInfo;
}

const FILTER_KEY_HANDLERS: Record<string, (info: FilterSetInfo) => FilterSetInfo | null> = {
  checkDate: clearCheckDateFilter,
  "invoiceFromDate-invoiceToDate": clearInvoiceDateFilter,
  "startFromDate-startToDate": clearStartDateFilter,
  search: clearSearchFilter,
};

export function applyFilterKeyDeletion(filterInfo: FilterSetInfo, key: string): FilterSetInfo | null {
  const handler = FILTER_KEY_HANDLERS[key];

  if (handler) {
    return handler(filterInfo);
  }

  return clearArrayFilterKey(filterInfo, key);
}
